// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { paginateQuery } from '@/lib/paginateSupabase';
import * as XLSX from 'xlsx';
import { MessageCircle, RefreshCw, Search, ExternalLink, Eye, Check, CheckCheck, AlertTriangle, X, Download } from 'lucide-react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

type LogRow = {
  id: string;
  message_sid: string | null;
  content_sid: string | null;
  template_name: string | null;
  to_phone: string;
  from_phone: string | null;
  status: string;
  error_code: string | null;
  error_message: string | null;
  triggered_by: string | null;
  cohort_id: string | null;
  user_id: string | null;
  created_at: string;
  updated_at: string;
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-slate-500/20 text-slate-500 border-slate-500/30',
  queued: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
  sent: 'bg-cyan-500/20 text-cyan-500 border-cyan-500/30',
  delivered: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30',
  read: 'bg-emerald-600/20 text-emerald-600 border-emerald-600/30',
  failed: 'bg-destructive/20 text-destructive border-destructive/30',
  undelivered: 'bg-amber-500/20 text-amber-500 border-amber-500/30',
  dlq: 'bg-destructive/20 text-destructive border-destructive/30',
};

const TEMPLATE_LABELS: Record<string, string> = {
  certificate_available: 'Certificado disponible',
  challenge_lab_reminder: 'Recordatorio Laboratorio de Retos',
  cohort_closing_pending_items: 'Cierre de cohorte: pendientes',
  content_reminder_before_session: 'Recordatorio de contenido antes de la sesión',
  deliverable_missing_24h: 'Entregable pendiente (24 h)',
  project_deadline_1_week: 'Proyecto: falta 1 semana',
  project_deadline_tomorrow: 'Proyecto: vence mañana',
};

function prettifyTemplate(alias: string) {
  const s = alias.replace(/_/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function templateLabel(alias: string | null) {
  if (!alias) return '—';
  return TEMPLATE_LABELS[alias] ?? prettifyTemplate(alias);
}

// Últimos 10 dígitos del número, sin prefijos ni signos
// Traducción de códigos de error de Twilio/WhatsApp a lenguaje natural
const ERROR_LABELS: Record<string, { label: string; hint: string }> = {
  '63024': {
    label: 'Número no válido en WhatsApp',
    hint: 'El número no tiene WhatsApp activo o Meta lo rechaza como destinatario. Verificar el teléfono del participante.',
  },
  '63049': {
    label: 'Meta bloqueó el mensaje de marketing',
    hint: 'Meta decidió no entregar la plantilla de tipo marketing (bloqueada en EE. UU. y limitada para usuarios poco activos). Usar una plantilla de utilidad.',
  },
  '63016': {
    label: 'Fuera de la ventana de 24 h',
    hint: 'Se envió texto libre sin plantilla aprobada fuera de la ventana de conversación de 24 horas.',
  },
  '63018': {
    label: 'Límite de envíos alcanzado',
    hint: 'Se superó el límite de mensajes permitido por Meta. Reintentar más tarde.',
  },
  '63003': {
    label: 'Destinatario no encontrado',
    hint: 'El canal no pudo ubicar al destinatario.',
  },
  '63005': {
    label: 'Destinatario no encontrado',
    hint: 'El canal no pudo ubicar al destinatario.',
  },
  '63007': {
    label: 'Remitente de WhatsApp no válido',
    hint: 'El número emisor configurado no está habilitado para WhatsApp.',
  },
  '63021': {
    label: 'Contenido del mensaje no válido',
    hint: 'Los parámetros o variables de la plantilla son incorrectos.',
  },
  '63032': {
    label: 'Usuario bloqueó a la empresa',
    hint: 'El destinatario bloqueó el número de WhatsApp de la organización.',
  },
  '502': {
    label: 'Error de conexión con Twilio',
    hint: 'Fallo temporal al llamar al servicio de envío. Se puede reintentar.',
  },
  '21211': {
    label: 'Número mal formado',
    hint: 'El teléfono no cumple el formato internacional (+57...).',
  },
  '21610': {
    label: 'Usuario dado de baja',
    hint: 'El destinatario respondió STOP y ya no recibe mensajes.',
  },
};

function errorInfo(code: string | null, message: string | null) {
  if (!code && !message) return { label: null as string | null, hint: '' };
  const known = code ? ERROR_LABELS[String(code)] : undefined;
  if (known) {
    return { label: known.label, hint: message ? `${known.hint} (${message})` : known.hint };
  }
  if (code) {
    return {
      label: `Error de envío (código ${code})`,
      hint: message ?? 'Código de error no catalogado. Consultar la documentación de Twilio.',
    };
  }
  return { label: message, hint: message ?? '' };
}

function normalizePhone(raw?: string | null): string | null {
  if (!raw) return null;
  const digits = String(raw).replace(/^whatsapp:/i, '').replace(/\D/g, '');
  if (digits.length < 7) return null;
  return digits.slice(-10);
}

export default function WhatsAppLogsPage() {
  const [rows, setRows] = useState<LogRow[]>([]);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [cohorts, setCohorts] = useState<any[]>([]);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [templateFilter, setTemplateFilter] = useState<string>('all');
  const [orgFilter, setOrgFilter] = useState<string>('all');
  const [cohortFilter, setCohortFilter] = useState<string>('all');
  const [identifiedFilter, setIdentifiedFilter] = useState<string>('all');
  const [errorFilter, setErrorFilter] = useState<string>('all');
  const [chatRow, setChatRow] = useState<LogRow | null>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatBody, setChatBody] = useState<string | null>(null);
  const [chatError, setChatError] = useState<string | null>(null);

  const openChat = async (row: LogRow) => {
    setChatRow(row);
    setChatBody(null);
    setChatError(null);
    if (!row.message_sid) {
      setChatError('Este envío no tiene Message SID (nunca llegó a salir hacia Twilio).');
      return;
    }
    setChatLoading(true);
    const { data, error } = await supabase.functions.invoke('get-whatsapp-message', {
      body: { message_sid: row.message_sid },
    });
    setChatLoading(false);
    if (error || data?.error) {
      setChatError(data?.error || error?.message || 'No se pudo traer el mensaje desde Twilio.');
      return;
    }
    setChatBody(data?.body ?? '(Mensaje sin texto — probablemente solo multimedia)');
  };

  const [refLoading, setRefLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const scopeSelected = cohortFilter !== 'all' || orgFilter !== 'all';

  useEffect(() => {
    (async () => {
      setRefLoading(true);
      try {
        const [allProfiles, allCohorts, allOrgs] = await Promise.all([
          paginateQuery<any>((from, to) =>
            supabase.from('profiles').select('id, full_name, email, phone, company, organization_id').range(from, to)),
          paginateQuery<any>((from, to) =>
            supabase.from('cohorts').select('id, name, organization_id').range(from, to)),
          paginateQuery<any>((from, to) =>
            supabase.from('organizations').select('id, name').range(from, to)),
        ]);
        setProfiles(allProfiles);
        setCohorts(allCohorts);
        setOrgs(allOrgs);
      } catch (e) {
        console.error(e);
      } finally {
        setRefLoading(false);
      }
    })();
  }, []);

  const loadLogs = async () => {
    if (!scopeSelected) { setRows([]); return; }
    let ids: string[] | null = null;
    if (cohortFilter !== 'all' && cohortFilter !== 'none') ids = [cohortFilter];
    else if (cohortFilter === 'all') {
      if (refLoading) return;
      ids = cohorts.filter(c => c.organization_id === orgFilter).map(c => c.id);
      if (ids.length === 0) { setRows([]); return; }
    }
    setLoading(true);
    try {
      const data = await paginateQuery<LogRow>((from, to) => {
        let q: any = supabase.from('whatsapp_send_log').select('*');
        q = ids ? q.in('cohort_id', ids) : q.is('cohort_id', null);
        return q.order('created_at', { ascending: false }).range(from, to);
      });
      setRows(data);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(); /* eslint-disable-next-line */ }, [cohortFilter, orgFilter, refLoading]);

  const enriched = useMemo(() => {
    const profileById = new Map(profiles.map(p => [p.id, p]));
    const profileByPhone = new Map<string, any>();
    for (const p of profiles) {
      const k = normalizePhone(p.phone);
      if (k && !profileByPhone.has(k)) profileByPhone.set(k, p);
    }
    const cohortById = new Map(cohorts.map(c => [c.id, c]));
    const orgById = new Map(orgs.map(o => [o.id, o]));

    return rows.map(r => {
      const person = (r.user_id ? profileById.get(r.user_id) : null)
        ?? profileByPhone.get(normalizePhone(r.to_phone) ?? '__none__')
        ?? null;
      const cohort = r.cohort_id ? cohortById.get(r.cohort_id) : null;
      const orgId = cohort?.organization_id ?? person?.organization_id ?? null;
      const orgName = (orgId ? orgById.get(orgId)?.name : null) ?? person?.company ?? null;
      return {
        ...r,
        person,
        person_name: person?.full_name ?? null,
        person_email: person?.email ?? null,
        org_id: orgId,
        org_name: orgName,
        cohort_name: cohort?.name ?? null,
        template_label: templateLabel(r.template_name),
        error_label: errorInfo(r.error_code, r.error_message).label,
        error_hint: errorInfo(r.error_code, r.error_message).hint,
      };
    });
  }, [rows, profiles, cohorts, orgs]);

  const stats = useMemo(() => {
    const total = rows.length;
    const counts = {
      pending: 0, queued: 0, sent: 0, delivered: 0, read: 0,
      failed: 0, undelivered: 0, dlq: 0,
    } as Record<string, number>;
    for (const r of rows) counts[r.status] = (counts[r.status] ?? 0) + 1;
    const deliveredOrRead = (counts.delivered ?? 0) + (counts.read ?? 0);
    const failedOrUndelivered = (counts.failed ?? 0) + (counts.undelivered ?? 0);
    const deliveryRate = total > 0 ? ((deliveredOrRead / total) * 100).toFixed(1) : '—';
    return { total, counts, deliveredOrRead, failedOrUndelivered, deliveryRate };
  }, [rows]);

  const uniqueTemplates = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.template_name) set.add(r.template_name);
    return Array.from(set).sort((a, b) => templateLabel(a).localeCompare(templateLabel(b)));
  }, [rows]);

  const uniqueOrgs = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of orgs) if (o.id && o.name) map.set(o.id, o.name);
    for (const r of enriched) if (r.org_id && r.org_name && !map.has(r.org_id)) map.set(r.org_id, r.org_name);
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [enriched, orgs]);

  const uniqueCohorts = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cohorts) {
      if (!c.id || !c.name) continue;
      if (orgFilter !== 'all' && c.organization_id !== orgFilter) continue;
      map.set(c.id, c.name);
    }
    for (const r of enriched) {
      if (!r.cohort_id || !r.cohort_name || map.has(r.cohort_id)) continue;
      if (orgFilter !== 'all' && r.org_id !== orgFilter) continue;
      map.set(r.cohort_id, r.cohort_name);
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [enriched, cohorts, orgFilter]);

  const uniqueErrors = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of enriched) {
      if (!r.error_label) continue;
      map.set(r.error_label, r.error_label);
    }
    return Array.from(map.keys()).sort((a, b) => a.localeCompare(b));
  }, [enriched]);

  const filtered = useMemo(() => {
    return enriched.filter(r => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (templateFilter !== 'all' && r.template_name !== templateFilter) return false;
      if (orgFilter !== 'all' && r.org_id !== orgFilter) return false;
      if (cohortFilter === 'none' && r.cohort_id) return false;
      if (cohortFilter !== 'all' && cohortFilter !== 'none' && r.cohort_id !== cohortFilter) return false;
      if (identifiedFilter === 'identified' && !r.person) return false;
      if (identifiedFilter === 'unknown' && r.person) return false;
      if (errorFilter === 'none' && r.error_label) return false;
      if (errorFilter === 'any' && !r.error_label) return false;
      if (errorFilter !== 'all' && errorFilter !== 'none' && errorFilter !== 'any' && r.error_label !== errorFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = [
          r.to_phone, r.message_sid, r.template_name, r.template_label, r.triggered_by,
          r.error_message, r.error_code, r.error_label, r.person_name, r.person_email, r.org_name, r.cohort_name,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [enriched, statusFilter, templateFilter, orgFilter, cohortFilter, identifiedFilter, errorFilter, search]);

  useEffect(() => { setPage(1); }, [statusFilter, templateFilter, orgFilter, cohortFilter, identifiedFilter, errorFilter, search]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const hasActiveFilters =
    statusFilter !== 'all' || templateFilter !== 'all' || orgFilter !== 'all' ||
    cohortFilter !== 'all' || identifiedFilter !== 'all' || errorFilter !== 'all' || search !== '';

  const clearFilters = () => {
    setStatusFilter('all');
    setTemplateFilter('all');
    setOrgFilter('all');
    setCohortFilter('all');
    setIdentifiedFilter('all');
    setErrorFilter('all');
    setSearch('');
  };

  const exportToExcel = () => {
    if (!filtered.length) return;
    const exportRows = filtered.map(r => ({
      Fecha: format(parseISO(r.created_at), 'dd/MM/yyyy HH:mm', { locale: es }),
      Status: r.status,
      Nombre: r.person_name ?? '',
      Correo: r.person_email ?? '',
      Empresa: r.org_name ?? '',
      Cohorte: r.cohort_name ?? '',
      Plantilla: r.template_label,
      Destino: r.to_phone,
      Trigger: r.triggered_by ?? '',
      Error: r.error_label ?? '',
      'Detalle del error': r.error_message ?? '',
      'Message SID': r.message_sid ?? '',
    }));
    const ws = XLSX.utils.json_to_sheet(exportRows);
    ws['!cols'] = [
      { wch: 17 }, { wch: 12 }, { wch: 28 }, { wch: 30 }, { wch: 30 },
      { wch: 30 }, { wch: 40 }, { wch: 15 }, { wch: 20 }, { wch: 32 }, { wch: 45 }, { wch: 38 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Envíos WhatsApp');
    XLSX.writeFile(wb, `whatsapp_send_log_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-primary" />
            WhatsApp Send Log
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Historial de envíos vía Twilio con status callback en vivo. Elegí una empresa o cohorte para cargar todos sus envíos.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={exportToExcel} disabled={!filtered.length || loading || refLoading}>
            <Download className="w-4 h-4 mr-2" />
            Exportar Excel
          </Button>
          <Button variant="outline" onClick={loadLogs} disabled={loading || !scopeSelected}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refrescar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard label="Total" value={String(stats.total)} />
        <StatCard label="Delivered/Read" value={String(stats.deliveredOrRead)} tone="emerald" />
        <StatCard label="Failed" value={String(stats.failedOrUndelivered)} tone="destructive" />
        <StatCard label="En cola" value={String((stats.counts.pending ?? 0) + (stats.counts.queued ?? 0))} tone="blue" />
        <StatCard label="Enviados" value={String(stats.counts.sent ?? 0)} tone="cyan" />
        <StatCard label="Delivery rate" value={`${stats.deliveryRate}${typeof stats.deliveryRate === 'string' && stats.deliveryRate !== '—' ? '%' : ''}`} tone="emerald" />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-lg">Filtros</CardTitle>
              <CardDescription>Buscá por persona, número, SID, plantilla, empresa, cohorte o error.</CardDescription>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Limpiar filtros
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="read">Read</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="undelivered">Undelivered</SelectItem>
              </SelectContent>
            </Select>
            <Select value={templateFilter} onValueChange={setTemplateFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las plantillas</SelectItem>
                {uniqueTemplates.map(t => <SelectItem key={t} value={t}>{templateLabel(t)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select
              value={orgFilter}
              onValueChange={(v) => { setOrgFilter(v); setCohortFilter('all'); }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las empresas</SelectItem>
                {uniqueOrgs.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={cohortFilter} onValueChange={setCohortFilter}>
              <SelectTrigger><SelectValue placeholder="Elegí una cohorte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{orgFilter !== 'all' ? 'Todas las cohortes de la empresa' : 'Elegí una cohorte'}</SelectItem>
                {orgFilter === 'all' && <SelectItem value="none">Sin cohorte</SelectItem>}
                {uniqueCohorts.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={identifiedFilter} onValueChange={setIdentifiedFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Identificados y no identificados</SelectItem>
                <SelectItem value="identified">Solo identificados</SelectItem>
                <SelectItem value="unknown">Solo sin identificar</SelectItem>
              </SelectContent>
            </Select>
            <Select value={errorFilter} onValueChange={setErrorFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los errores</SelectItem>
                <SelectItem value="none">Sin error</SelectItem>
                <SelectItem value="any">Con cualquier error</SelectItem>
                {uniqueErrors.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Envíos ({filtered.length} de {enriched.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {!scopeSelected ? (
            <p className="text-center text-muted-foreground py-8">Elegí una empresa o una cohorte para ver sus envíos.</p>
          ) : loading || refLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Sin envíos que matcheen los filtros.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Correo</TableHead>
                    <TableHead>Empresa / Cohorte</TableHead>
                    <TableHead>Plantilla</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead>Message SID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map(r => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs">
                        <div>{format(parseISO(r.created_at), 'd MMM HH:mm', { locale: es })}</div>
                        <div className="text-muted-foreground">{formatDistanceToNow(parseISO(r.updated_at), { locale: es, addSuffix: true })}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[r.status] || ''}>{r.status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.person_name
                          ? <span className="font-medium">{r.person_name}</span>
                          : <span className="text-muted-foreground italic">Sin identificar</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.person_email ?? <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{r.org_name ?? '—'}</div>
                        <div className="text-muted-foreground">{r.cohort_name ?? '—'}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{r.template_label}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">{r.template_name ?? '—'}</div>
                      </TableCell>
                      <TableCell className="text-xs font-mono">{r.to_phone}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.triggered_by ?? '—'}</TableCell>
                      <TableCell className="text-xs max-w-xs">
                        {r.error_label ? (
                          <div title={r.error_hint}>
                            <div className="text-destructive">{r.error_label}</div>
                            {r.error_code && (
                              <div className="text-[10px] font-mono text-muted-foreground">Código {r.error_code}</div>
                            )}
                          </div>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {r.message_sid ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openChat(r)}
                              title="Ver mensaje"
                              className="inline-flex items-center gap-1 text-primary hover:underline"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              Ver
                            </button>
                            <a
                              href={`https://console.twilio.com/us1/monitor/logs/sms?frameUrl=%2Fconsole%2Fsms%2Flogs%2F${r.message_sid}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-muted-foreground hover:underline"
                            >
                              {r.message_sid.slice(0, 10)}…
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-4">
                  <Button variant="outline" size="sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
                    Anterior
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Página {safePage} de {totalPages} ({filtered.length} envíos)
                  </span>
                  <Button variant="outline" size="sm" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>
                    Siguiente
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vista tipo chat: trae el texto REAL desde Twilio (message_sid), no lo reconstruye local */}
      <Dialog open={!!chatRow} onOpenChange={(o) => !o && setChatRow(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <MessageCircle className="w-4 h-4 text-emerald-500" />
              {chatRow?.to_phone}
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-lg bg-[#e5ddd5] dark:bg-muted/30 p-4 min-h-[140px] flex flex-col justify-end">
            {chatLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-16 w-3/4 ml-auto rounded-lg" />
              </div>
            ) : chatError ? (
              <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{chatError}</span>
              </div>
            ) : (
              <div className="ml-auto max-w-[85%] bg-[#dcf8c6] dark:bg-emerald-900/40 rounded-lg rounded-tr-none px-3 py-2 shadow-sm">
                <p className="text-sm whitespace-pre-wrap text-foreground/90 dark:text-foreground">{chatBody}</p>
                <div className="flex items-center justify-end gap-1 mt-1">
                  <span className="text-[10px] text-muted-foreground">
                    {chatRow && format(parseISO(chatRow.created_at), 'HH:mm', { locale: es })}
                  </span>
                  {chatRow && (chatRow.status === 'read' ? (
                    <CheckCheck className="w-3.5 h-3.5 text-blue-500" />
                  ) : chatRow.status === 'delivered' ? (
                    <CheckCheck className="w-3.5 h-3.5 text-muted-foreground" />
                  ) : chatRow.status === 'sent' ? (
                    <Check className="w-3.5 h-3.5 text-muted-foreground" />
                  ) : null)}
                </div>
              </div>
            )}
          </div>
          {chatRow && (
            <p className="text-xs text-muted-foreground">
              Plantilla: <span>{templateLabel(chatRow.template_name)}</span> · Trigger: {chatRow.triggered_by ?? '—'}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: string; tone?: 'emerald' | 'destructive' | 'blue' | 'cyan' }) {
  const toneClass =
    tone === 'emerald' ? 'text-emerald-500' :
    tone === 'destructive' ? 'text-destructive' :
    tone === 'blue' ? 'text-blue-500' :
    tone === 'cyan' ? 'text-cyan-500' : '';
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold ${toneClass}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
